import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { IInventoryOption } from '../../../Interfaces/IInventoryOption';
import { ILegalClient } from '../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipRealEstate } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { IPropertyType } from '../../../Interfaces/IPropertyType';
import { IPurchaseContractRelationType } from '../../../Interfaces/IPurchaseContractRelationType';
import { IRealEstateUseType } from '../../../Interfaces/IRealEstateUseType';
import {
  removeFirstManager,
  updateNames,
  validateAddress,
  validateArea,
} from '../utilities';
import {
  IRealEstatePurchaseContract,
  initRealEstatePurchaseContract,
} from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/IRealEstatePurchaseContract';
import { RealEstateInventoryOptions } from './InventoryOption/RealEstateInventoryOptions';
import { RealEstateAddress } from './RealEstateAddress';
import { RealEstateManager } from './RealEstateManager';
import { RealEstatePriceUseType } from './RealEstatePriceUseType';
import { RealEstatePropertyTypes } from './RealEstatePropertyTypes';

interface IProps {
  relationship: ILegalTransactionRelationship;
  unmount: () => void;
  setRelationship: (newValue: ILegalTransactionRelationship) => void;
  editedRealEstate: ILegalTransactionRelationshipRealEstate;
  propertyTypes: IPropertyType[];
  purchaseContractRelationTypes: IPurchaseContractRelationType[];
  inventoryOptions: IInventoryOption[];
  realEstateUseTypes: IRealEstateUseType[];
  setMembers: (newMembers: ILegalClient[]) => void;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
}

export const EditRealEstateDialog: React.FC<IProps> = ({
  unmount,
  setRelationship,
  relationship,
  editedRealEstate,
  propertyTypes,
  purchaseContractRelationTypes,
  inventoryOptions,
  realEstateUseTypes,
  setMembers,
  takenLegalClients,
  setTakenLegalClients,
}) => {
  const [formValues, setFormValues] = useState(editedRealEstate);
  const [savedManager, setSavedManager] = useState<ILegalClient>();
  const [manager, setManager] = useState<ILegalClient>();
  const [isWarningPrice, setIsWarningPrice] = useState(false);
  const [purchaseContract, setPurchaseContract] =
    useState<IRealEstatePurchaseContract>(
      () =>
        editedRealEstate.PurchaseContractArray?.[0] ??
        initRealEstatePurchaseContract(editedRealEstate.idRealEstate, -1)
    );

  useEffect(() => {
    if (!editedRealEstate.BuildingManager_idLegalClient) return;
    const member = relationship.Members.find(
      m =>
        m.idRelationshipRole === 3 &&
        m.idLegalClient === editedRealEstate.BuildingManager_idLegalClient
    );

    if (!member) return;

    setSavedManager(cloneDeep(member.LegalClient));
    setManager(cloneDeep(member.LegalClient));
  }, []);

  const hasNoPropertyType = useMemo(
    () => formValues.PropertyTypeArray.length === 0,
    [formValues.PropertyTypeArray]
  );

  const hasBuildPropertyType = useMemo(
    () => formValues.PropertyTypeArray.some(x => x.idPropertyType === 1),
    [formValues.PropertyTypeArray]
  );

  const isOnlyUndevelopedProperty = useMemo(
    () =>
      formValues.PropertyTypeArray.length === 1 &&
      formValues.PropertyTypeArray.some(x => x.idPropertyType === 2),
    [formValues.PropertyTypeArray]
  );

  const isAddressValid = useMemo(
    () => validateAddress(formValues),
    [
      formValues.Street,
      formValues.StreetNr,
      formValues.idPostcode,
      formValues.Latitude,
      formValues.Longitude,
    ]
  );

  const isAreaValid = useMemo(
    () => validateArea(formValues, purchaseContract),
    [
      formValues.hasPartialArea,
      formValues.isMeasured,
      formValues.Area,
      purchaseContract,
    ]
  );

  const onSubmit = useCallback(() => {
    // Remove the old manager from the relationship if there was one
    let otherManagers = [...relationship.Members];

    if (savedManager) {
      otherManagers = removeFirstManager(
        relationship.Members,
        savedManager.idLegalClient
      );
    }

    setRelationship({
      ...relationship,
      RealEstateArray: [
        ...relationship.RealEstateArray.map(re =>
          re.idRealEstate === formValues.idRealEstate ? formValues : re
        ),
      ],
      Members: [
        ...otherManagers,
        ...(manager
          ? [
              {
                idRelationship: relationship.idRelationship,
                idRelationshipRole: 3,
                idLegalClient: manager.idLegalClient,
                FirstName: manager.FirstName || '',
                LastName: manager.LastName,
                LegalClient: manager,
                LegalTransactionArray: [],
                isActiveLegalClientRelationship: true,
                idLegalClientRelationship: -1,
              },
            ]
          : []),
      ],
    });

    unmount();
  }, [relationship, formValues, manager, setRelationship, unmount]);

  return (
    <Dialog open={true} onClose={unmount} maxWidth='lg' fullWidth>
      <DialogTitle>Objekt Bearbeiten</DialogTitle>
      <DialogContent>
        <RealEstateAddress
          formValues={formValues}
          setFormValues={setFormValues}
        />
        <Typography variant='h5' sx={{ mt: 10, mb: 3 }}>
          Wohn- oder Teileigentum
        </Typography>
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <FormControl fullWidth required>
              <InputLabel>Wohn- oder Teileigentum</InputLabel>
              <Select
                label='Wohn- oder Teileigentum'
                size='small'
                fullWidth
                value={formValues.hasApartment ? 1 : 2}
                onChange={({ target: { value } }) =>
                  setFormValues(x => ({
                    ...x,
                    hasApartment: Number(value) === 1,
                  }))
                }
              >
                <MenuItem key={1} value={1}>
                  Ja
                </MenuItem>
                <MenuItem key={2} value={2}>
                  Nein
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item sm={12}>
            <Collapse in={!!formValues.hasApartment}>
              <RealEstateManager
                takenLegalClients={takenLegalClients}
                setTakenLegalClients={setTakenLegalClients}
                manager={manager ? [manager] : []}
                setManager={newValue => {
                  setRelationship({
                    ...relationship,
                    Members: updateNames(relationship.Members, newValue),
                  });

                  const newManager =
                    newValue.length > 0 ? newValue[0] : undefined;

                  setManager(newManager);
                  setFormValues(x => ({
                    ...x,
                    BuildingManager_idLegalClient: newManager
                      ? newManager.idLegalClient
                      : null,
                  }));
                }}
              />
            </Collapse>
          </Grid>
        </Grid>
        <Box sx={{ mt: 10 }}>
          {/*
          <RealEstateLandRegister
            currentObject={formValues}
            setCurrentObject={setFormValues}
          />
           */}
        </Box>
        <Box sx={{ mt: 10 }}>
          <RealEstatePropertyTypes
            formValues={formValues}
            setFormValues={setFormValues}
            propertyTypes={propertyTypes}
          />
        </Box>
        {!isOnlyUndevelopedProperty && (
          <>
            <Typography variant='h5' sx={{ mt: 10, mb: 3 }}>
              Bauten
            </Typography>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <Collapse in={!hasBuildPropertyType}>
                  <FormControl fullWidth required>
                    <InputLabel>Bebaut</InputLabel>
                    <Select
                      label='Bebaut'
                      size='small'
                      fullWidth
                      value={formValues.isBuild ? 1 : 2}
                      onChange={({ target: { value } }) =>
                        setFormValues(x => ({
                          ...x,
                          isBuild: Number(value) === 1,
                          BuildWith:
                            Number(value) === 1 ? formValues.BuildWith : null,
                        }))
                      }
                    >
                      <MenuItem key={1} value={1}>
                        Ja
                      </MenuItem>
                      <MenuItem key={2} value={2}>
                        Nein
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Collapse>
              </Grid>
              <Grid item sm={12}>
                <Collapse in={!!formValues.isBuild || hasBuildPropertyType}>
                  <TextField
                    sx={{ mt: 3 }}
                    label='Bauten'
                    size='small'
                    fullWidth
                    multiline
                    rows={3}
                    value={formValues.BuildWith ?? ''}
                    onChange={({
                      target,
                    }: React.ChangeEvent<HTMLInputElement>) =>
                      setFormValues(x => ({ ...x, BuildWith: target.value }))
                    }
                  />
                </Collapse>
              </Grid>
            </Grid>
          </>
        )}
        <Typography variant='h5' sx={{ mt: 10, mb: 3 }}>
          Teilfläche
        </Typography>

        <FormControl fullWidth required>
          <InputLabel>Fläche</InputLabel>
          <Select
            label='Fläche'
            size='small'
            fullWidth
            value={formValues.hasPartialArea ? 1 : 2}
            onChange={({ target: { value } }) =>
              setFormValues(x => ({
                ...x,
                hasPartialArea: Number(value) === 1,
              }))
            }
          >
            <MenuItem key={1} value={1}>
              Teilbereich
            </MenuItem>
            <MenuItem key={2} value={2}>
              Ganzes Grundstück
            </MenuItem>
          </Select>
        </FormControl>
        <Collapse in={!!formValues.hasPartialArea}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item sm={12}>
              <FormControl fullWidth required>
                <InputLabel>Vermessen</InputLabel>
                <Select
                  label='Vermessen'
                  size='small'
                  fullWidth
                  value={formValues.isMeasured ? 1 : 2}
                  onChange={({ target: { value } }) =>
                    setFormValues(x => ({
                      ...x,
                      isMeasured: Number(value) === 1,
                    }))
                  }
                >
                  <MenuItem key={1} value={1}>
                    Ja
                  </MenuItem>
                  <MenuItem key={2} value={2}>
                    Nein
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sm={6}>
              <Collapse in={!!formValues.isMeasured}>
                <NumericFormat
                  value={formValues.Area ?? ''}
                  suffix=' qm'
                  fixedDecimalScale
                  thousandsGroupStyle='thousand'
                  thousandSeparator='.'
                  decimalSeparator=','
                  fullWidth
                  customInput={TextField}
                  onValueChange={(values, _) => {
                    if (values.floatValue !== undefined) {
                      setFormValues(x => ({
                        ...x,
                        Area: values.floatValue ?? null,
                      }));
                    } else {
                      setFormValues(x => ({
                        ...x,
                        Area: null,
                      }));
                    }
                  }}
                  allowNegative={false}
                  label='Fläche'
                  size='small'
                  required
                />
              </Collapse>
              <Collapse in={!formValues.isMeasured}>
                <FormControl fullWidth required>
                  <InputLabel>Auftraggeber/inn</InputLabel>
                  <Select
                    label='Auftraggeber/inn'
                    size='small'
                    fullWidth
                    value={
                      purchaseContract.OrderMeasurement_idPurchaseContractRelationType ??
                      ''
                    }
                    onChange={({ target: { value } }) =>
                      setPurchaseContract(x => ({
                        ...x,
                        OrderMeasurement_idPurchaseContractRelationType:
                          Number(value),
                      }))
                    }
                  >
                    {purchaseContractRelationTypes.map((x, i) => (
                      <MenuItem
                        key={i}
                        value={x.idPurchaseContractRelationType}
                      >
                        {x.PurchaseContractRelationType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Collapse>
            </Grid>

            <Grid item sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Zahlungspflichtiger</InputLabel>
                <Select
                  label='Zahlungspflichtiger'
                  size='small'
                  fullWidth
                  value={
                    purchaseContract.PaymentMeasurement_idPurchaseContractRelationType ??
                    purchaseContractRelationTypes[0]
                      .idPurchaseContractRelationType
                  }
                  onChange={({ target: { value } }) =>
                    setPurchaseContract(x => ({
                      ...x,
                      PaymentMeasurement_idPurchaseContractRelationType:
                        Number(value),
                    }))
                  }
                >
                  {purchaseContractRelationTypes.map((x, i) => (
                    <MenuItem key={i} value={x.idPurchaseContractRelationType}>
                      {x.PurchaseContractRelationType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item sm={12}>
              <TextField
                label='Dienstbarkeiten'
                value={formValues.hasEasement ? 'true' : 'false'}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFormValues(x => ({
                    ...x,
                    hasEasement: event.target.value === 'true',
                  }))
                }
                fullWidth
                size='small'
                select
              >
                <MenuItem key='hasEasement-true' value='true'>
                  Ja
                </MenuItem>
                <MenuItem key='hasEasement-false' value='false'>
                  Nein
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item sm={12}>
              <Collapse in={!!formValues.hasEasement}>
                <TextField
                  sx={{ mt: 1 }}
                  label='Dienstbarkeiten'
                  value={
                    formValues.EasementText === null
                      ? ''
                      : formValues.EasementText
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setFormValues(x => ({
                      ...x,
                      EasementText: event.target.value,
                    }))
                  }
                  size='small'
                  fullWidth
                  multiline
                  rows={3}
                />
              </Collapse>
            </Grid>
          </Grid>
        </Collapse>
        <Box sx={{ mt: 10 }}>
          <RealEstateInventoryOptions
            inventoryOptionArray={purchaseContract.InventoryOptionArray}
            setInventoryOptionArray={arr =>
              setPurchaseContract(x => ({ ...x, InventoryOptionArray: arr }))
            }
            inventoryOptions={inventoryOptions}
          />
        </Box>
        <Box sx={{ mt: 10 }}>
          <RealEstatePriceUseType
            formValues={formValues}
            setFormValues={setFormValues}
            purchaseContract={purchaseContract}
            setPurchaseContract={setPurchaseContract}
            realEstateUseTypes={realEstateUseTypes}
            isWarningPrice={isWarningPrice}
            setIsWarningPrice={setIsWarningPrice}
            setMembers={setMembers}
            takenLegalClients={takenLegalClients}
            setTakenLegalClients={setTakenLegalClients}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          float: 'right',
        }}
      >
        <Button variant='outlined' onClick={unmount} sx={{ mr: 2 }}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          onClick={onSubmit}
          disabled={
            !isAddressValid ||
            !isAreaValid ||
            hasNoPropertyType ||
            isWarningPrice
          }
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
